import { GalleryFileItem, GalleryMediaPayload, GallerySourceImage } from "./types";

export type UploadPathSegments = {
  propertyName?: string;
  unitCode?: string;
};

export const slugifyPathSegment = (value?: string) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const buildUploadPath = (orgName?: string, segments?: UploadPathSegments) =>
  [slugifyPathSegment(orgName), slugifyPathSegment(segments?.propertyName), slugifyPathSegment(segments?.unitCode)]
    .filter(Boolean)
    .join("/");

/** Store `{pathPrefix}/{fileName}` instead of full upload URL — matches gallery save payload. */
export const toRelativeDocumentUrl = (sourceUrl: string, pathPrefix?: string) => {
  const url = String(sourceUrl || "").trim();
  if (!url) return "";
  const fileName = url.split("/").pop() || "";
  if (pathPrefix && fileName) {
    return `${pathPrefix.replace(/\/+$/, "")}/${fileName}`;
  }
  return url;
};

export const reorderList = <T,>(list: T[], startIndex: number, endIndex: number): T[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export const getMimeFromName = (name = "") => {
  const ext = name.split(".").pop() || "jpg";
  return ext.toLowerCase();
};

export const toGalleryImagesPayload = (
  files: GalleryFileItem[],
  pathPrefix?: string,
) =>
  files
    .filter((file) => Boolean(file.documentId && (file.documentUrl || file.url)))
    .map((file, index) => {
      const sourceUrl = (file.documentUrl || file.url || "") as string;

      return {
        documentId: file.documentId as string,
        documentUrl: toRelativeDocumentUrl(sourceUrl, pathPrefix),
        position: String(index + 1),
      };
    });

/** Hero image = gallery item saved at position 1 with a document URL. */
export const hasHeroGalleryImage = (
  galleryImages?: { position?: string | number; documentUrl?: string }[],
) =>
  Boolean(
    galleryImages?.find(
      (image) => String(image.position) === "1" && String(image.documentUrl || "").trim(),
    ),
  );

export const sortSourceImages = (images: GallerySourceImage[] = []) => {
  return [...images].sort((a, b) => {
    const aPos = Number(a.position ?? a.order ?? 0);
    const bPos = Number(b.position ?? b.order ?? 0);
    return aPos - bPos;
  });
};

export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

export const canvasToBlob = (canvas: HTMLCanvasElement, type = "image/jpeg", quality = 0.95) => {
  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
};
